import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ops, asList, fmtDate, OPS } from "@/admin/api";
import { PageTitle, EmptyState, Pill, ActionButton, Table, cell, StatCard } from "@/admin/ui";

const voucherStatus = (v) => {
  if (v.disabled || v.revoked || v.status === "disabled") return { label: "Disabled", tone: "red" };
  const max = v.maxRedemptions ?? v.max_redemptions ?? v.usageLimit;
  const used = v.redeemedCount ?? v.redeemed_count ?? v.redemption_count ?? v.usedCount ?? 0;
  if (v.status === "Depleted" || (max != null && used >= max)) return { label: "Depleted", tone: "amber" };
  if (v.status === "Expired") return { label: "Expired", tone: "amber" };
  const expires = v.expiresAt || v.expires_at;
  const created = v.createdAt || v.created_at;
  const days = v.durationDays ?? v.duration_days ?? v.duration;
  const expiry = expires ? new Date(expires) : created && days ? new Date(new Date(created).getTime() + days * 86400000) : null;
  if (expiry && expiry.getTime() < Date.now()) return { label: "Expired", tone: "amber" };
  return { label: "Active", tone: "green" };
};

export default function Membership() {
  const [subs, setSubs] = useState(null);
  const [vouchers, setVouchers] = useState(null);
  const [passes, setPasses] = useState(null);
  const [errors, setErrors] = useState({});
  const [duration, setDuration] = useState("30");
  const [maxRedemptions, setMaxRedemptions] = useState("100");
  const [customCode, setCustomCode] = useState("");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => {
    ops(OPS.subscribers).then((d) => setSubs(asList(d, "subscribers"))).catch((e) => setErrors((x) => ({ ...x, subs: e.message })));
    ops(OPS.vouchers).then((d) => setVouchers(asList(d, "vouchers", "codes"))).catch((e) => setErrors((x) => ({ ...x, vouchers: e.message })));
    ops(OPS.ridePasses).then((d) => setPasses(asList(d, "passes", "ridePasses"))).catch((e) => setErrors((x) => ({ ...x, passes: e.message })));
  };
  useEffect(load, []);

  const generate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await ops(OPS.createVoucher, {
        method: "POST",
        body: {
          duration_days: Number(duration),
          max_redemptions: Number(maxRedemptions),
          ...(customCode.trim() ? { code: customCode.trim().toUpperCase() } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      });
      toast.success("Voucher generated");
      setCustomCode("");
      setNotes("");
      setVouchers(null);
      load();
    } catch (err) {
      toast.error(`Generate failed: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const disable = async (v) => {
    const id = v.id || v.code;
    try {
      await ops(OPS.disableVoucher(id), { method: "POST" });
      toast.success(`Voucher ${v.code} disabled`);
      setVouchers(null);
      load();
    } catch (err) {
      toast.error(`Disable failed: ${err.message}`);
    }
  };

  return (
    <div data-testid="admin-membership-page">
      <PageTitle eyebrow="Admin Portal" title="Membership & Revenue" />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Active Premium subscribers" value={subs ? subs.length : errors.subs ? "—" : "…"} sub={errors.subs} accent="text-amber-300" testId="stat-subs" />
        <StatCard label="Vouchers" value={vouchers ? vouchers.length : errors.vouchers ? "—" : "…"} sub={errors.vouchers} testId="stat-vouchers" />
        <StatCard label="Ride Passes issued" value={passes ? passes.length : errors.passes ? "—" : "…"} sub={errors.passes} accent="text-[#00F0FF]" testId="stat-passes" />
      </div>

      <h2 className="label-tech mt-12 text-neutral-500">Voucher Management</h2>
      <div className="mt-4 rounded-xl border border-white/10 bg-[#0c0c0e] p-5" data-testid="voucher-create-card">
        <p className="font-display text-lg font-bold text-white">Create Voucher</p>
        <form onSubmit={generate} className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="create-voucher-form">
          <div>
            <label htmlFor="voucher-duration" className="label-tech mb-1.5 block text-[10px] text-neutral-500">Duration (days)</label>
            <input
              id="voucher-duration"
              type="number"
              min="1"
              required
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="h-10 w-full rounded-lg border border-white/10 bg-white/5 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF3333]"
              data-testid="voucher-duration-input"
            />
          </div>
          <div>
            <label htmlFor="voucher-max" className="label-tech mb-1.5 block text-[10px] text-neutral-500">Max Redemptions</label>
            <input
              id="voucher-max"
              type="number"
              min="1"
              required
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              className="h-10 w-full rounded-lg border border-white/10 bg-white/5 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF3333]"
              data-testid="voucher-max-input"
            />
          </div>
          <div>
            <label htmlFor="voucher-code" className="label-tech mb-1.5 block text-[10px] text-neutral-500">Custom Code <span className="normal-case text-neutral-600">(optional)</span></label>
            <input
              id="voucher-code"
              value={customCode}
              onChange={(e) => setCustomCode(e.target.value)}
              placeholder="Auto-generate if empty"
              className="h-10 w-full rounded-lg border border-white/10 bg-white/5 px-3 font-mono-plex text-sm uppercase text-white placeholder:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-[#FF3333]"
              data-testid="voucher-code-input"
            />
          </div>
          <div>
            <label htmlFor="voucher-notes" className="label-tech mb-1.5 block text-[10px] text-neutral-500">Notes</label>
            <input
              id="voucher-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Launch campaign"
              className="h-10 w-full rounded-lg border border-white/10 bg-white/5 px-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-[#FF3333]"
              data-testid="voucher-notes-input"
            />
          </div>
          <div className="sm:col-span-2 xl:col-span-4">
            <button
              type="submit"
              disabled={creating}
              className="h-10 rounded-full bg-[#FF3333] px-8 font-display text-sm font-bold text-white transition-colors duration-300 hover:bg-[#FF5C5C] disabled:opacity-60"
              data-testid="voucher-generate-btn"
            >
              {creating ? "Generating…" : "Generate"}
            </button>
          </div>
        </form>
      </div>

      <p className="label-tech mt-8 text-neutral-500">All Vouchers</p>
      <div className="mt-3">
        {!vouchers ? (
          errors.vouchers ? <EmptyState title="Voucher list unavailable" hint={errors.vouchers} /> : <p className="label-tech text-neutral-500">Loading…</p>
        ) : !vouchers.length ? (
          <EmptyState title="No vouchers yet" hint="Generated vouchers appear here with redemption counts and status." />
        ) : (
          <Table head={["Code", "Duration", "Max Redemptions", "Redeemed", "Status", "Created", "Notes", ""]} testId="vouchers-table">
            {vouchers.map((v, i) => {
              const st = voucherStatus(v);
              const id = v.id || v.code || i;
              return (
                <tr key={id} data-testid={`voucher-row-${id}`}>
                  <td className={`${cell} font-mono-plex font-semibold text-white`}>{v.code}</td>
                  <td className={cell}>{v.durationDays ?? v.duration_days ?? v.duration ?? "—"} days</td>
                  <td className={cell}>{v.maxRedemptions ?? v.max_redemptions ?? v.usageLimit ?? "—"}</td>
                  <td className={cell}>{v.redeemedCount ?? v.redeemed_count ?? v.usedCount ?? 0}</td>
                  <td className={cell}><Pill tone={st.tone}>{st.label}</Pill></td>
                  <td className={cell}>{fmtDate(v.createdAt || v.created_at)}</td>
                  <td className={`${cell} max-w-[180px] truncate`}>{v.notes || "—"}</td>
                  <td className={`${cell} text-right`}>
                    {st.label === "Active" && (
                      <ActionButton tone="red" onClick={() => disable(v)} data-testid={`voucher-disable-${id}`}>Disable</ActionButton>
                    )}
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </div>

      <h2 className="label-tech mt-12 text-neutral-500">Premium subscribers & expiry</h2>
      <div className="mt-4">
        {!subs ? (
          errors.subs ? <EmptyState title="Subscriber list unavailable" hint={errors.subs} /> : <p className="label-tech text-neutral-500">Loading…</p>
        ) : !subs.length ? (
          <EmptyState title="No active subscribers returned" />
        ) : (
          <Table head={["User", "Plan", "Period ends", "Status"]} testId="subs-table">
            {subs.map((s, i) => (
              <tr key={s.id || i} data-testid={`sub-row-${s.id || i}`}>
                <td className={`${cell} font-semibold text-white`}>{s.displayName || s.email || s.userId || "—"}</td>
                <td className={cell}>{s.plan || "Premium"}</td>
                <td className={cell}>{fmtDate(s.currentPeriodEnd || s.expiresAt)}</td>
                <td className={cell}><Pill tone="green">{s.status || "active"}</Pill></td>
              </tr>
            ))}
          </Table>
        )}
      </div>

      <h2 className="label-tech mt-12 text-neutral-500">Ride Pass expiry</h2>
      <div className="mt-4">
        {!passes ? (
          errors.passes ? <EmptyState title="Ride Pass data unavailable" hint={errors.passes} /> : <p className="label-tech text-neutral-500">Loading…</p>
        ) : !passes.length ? (
          <EmptyState title="No Ride Passes issued yet" />
        ) : (
          <Table head={["User", "Issued", "Expires", "Status"]} testId="passes-table">
            {passes.map((p, i) => (
              <tr key={p.id || i} data-testid={`pass-row-${p.id || i}`}>
                <td className={`${cell} font-semibold text-white`}>{p.displayName || p.email || p.userId || "—"}</td>
                <td className={cell}>{fmtDate(p.issuedAt || p.createdAt)}</td>
                <td className={cell}>{fmtDate(p.expiresAt)}</td>
                <td className={cell}><Pill tone={p.expired ? "red" : "cyan"}>{p.expired ? "expired" : "active"}</Pill></td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}
